import React, { useEffect, useRef, useState } from "react";
import {
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaPaperPlane,
  FaCheckCircle,
} from "react-icons/fa";
import MyPage from "../components/MyPage";

function ContactUs() {
  const [sent, setSent] = useState(false);
  const [data, setData] = useState(null);
  const sectionRef = useRef(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSent(true);
    setTimeout(() => {
      setSent(false);
      e.target.reset();
    }, 2500);
  };

  useEffect(() => {
    // ✅ JSON → UI
    fetch("/api/contact.json")
      .then((res) => res.json())
      .then((json) => setData(json));

    // ✅ XML → proof only
    fetch("/api/contact.xml")
      .then((res) => res.text())
      .then(() => console.log("Contact XML loaded"));
  }, []);

  useEffect(() => {
    const banner = document.querySelector(".inner-banner");
    banner?.classList.add("animate");

    if (!sectionRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          sectionRef.current.classList.add("in-view");
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );

    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [data]);

  if (!data) return null;

  return (
    <>
      {/* ✅ SEO – unchanged */}
      <MyPage
        title="Contact Us | Thrissur Jilla Pravasi Kootayma"
        description="Contact Thrissur Jilla Pravasi Kootayma for support, queries, and community engagement."
        keywords="Thrissur Pravasi contact, Pravasi Kootayma contact"
      />

      {/* ===== INNER BANNER ===== */}
      <section className="inner-banner">
        <div className="inner-overlay"></div>
        <div className="inner-content">
          <h1>{data.banner.title}</h1>
        </div>
        <div className="breadcrumb">{data.banner.breadcrumb}</div>
      </section>

      {/* ===== CONTACT SECTION ===== */}
      <section className="contact-section" ref={sectionRef}>
        <div className="contact-header">
          <h2>
            {data.header.title} <span></span>
          </h2>
          <p>{data.header.description}</p>
        </div>

        <div className="contact-wrapper">
          {/* LEFT */}
          <div className="contact-left">
            <div className="left-overlay"></div>
            <div className="left-content">
              <div className="left-item">
                <FaMapMarkerAlt />
                <h4>Address</h4>
                <p>{data.info.address}</p>
              </div>

              <div className="left-item">
                <FaPhoneAlt />
                <h4>Let’s Talk</h4>
                <p>{data.info.phone}</p>
              </div>

              <div className="left-item">
                <FaEnvelope />
                <h4>General Support</h4>
                <p>{data.info.email}</p>
              </div>
            </div>
          </div>

          {/* RIGHT FORM */}
          <form
            className={`contact-form ${sent ? "sent" : ""}`}
            onSubmit={handleSubmit}
          >
            {!sent ? (
              <>
                <h3>Send Us A Message</h3>
                <input type="text" placeholder="Your Name" required />
                <input type="email" placeholder="Your Email" required />
                <input type="text" placeholder="Phone Number" />
                <textarea placeholder="Your Message"></textarea>
                <button type="submit">
                  Send Message <FaPaperPlane />
                </button>
              </>
            ) : (
              <div className="success-box">
                <FaCheckCircle />
                <h3>Message Sent!</h3>
                <p>We will contact you soon 🚀</p>
              </div>
            )}
          </form>
        </div>
      </section>

      {/* ===== CSS ===== */}
      <style>{`
/* ===== INNER BANNER ===== */
.inner-banner {
  position: relative;
  height: 420px;
  background: url("/assets/banner.jpg") center/cover no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 120px;
  color: #fff;
}

.inner-overlay {
  
  inset: 0;
  background: rgba(0,0,0,0.65);
}

.inner-content {
  position: relative;
  z-index: 1;
}

.inner-content h1 {
  font-size: 48px;
  font-family: "Merriweather", serif;
}

.breadcrumb {
  position: absolute;
  bottom: -20px;
  left: 55px;
  border-radius: 4px;
  background: var(--primary-color);
  padding: 10px 25px;
  font-size: 20px;
  font-weight: 500;
  color: #000;
  z-index: 1;
}

/* ===== CONTACT SECTION ===== */
.contact-section {
  padding: 100px 80px;
  background: #fff;
  overflow-x: hidden;
}

.contact-header {
  text-align: center;
  max-width: 800px;
  margin: 0 auto 70px;
}

.contact-header h2 {
  font-size: 36px;
  font-family: "Merriweather", serif;
}

.contact-header h2 span {
  display: inline-block;
  width: 60px;
  height: 2px;
  background: #000;
  margin-left: 10px;
}

.contact-header p {
  margin-top: 15px;
  font-size: 16px;
}

/* ===== LAYOUT ===== */
.contact-wrapper {
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 520px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 45px rgba(0,0,0,0.2);
}

/* ===== LEFT SIDE ===== */
.contact-left {
  position: relative;
  background: url("/assets/contact-bg.jpg") center/cover no-repeat;
  color: #fff;
}

.left-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.7);
}

.left-content {
  position: relative;
  z-index: 2;
  padding: 60px;
  display: flex;
  flex-direction: column;
  gap: 35px;
}

.left-item svg {
  color: #2ecc71;
  font-size: 22px;
  margin-bottom: 8px;
}

.left-item h4 {
  font-size: 18px;
  margin-bottom: 4px;
}

.left-item p {
  font-size: 15px;
  opacity: 0.9;
}

/* ===== RIGHT FORM ===== */
.contact-form {
  background: #fff;
  padding: 60px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.contact-form h3 {
  font-size: 26px;
  margin-bottom: 10px;
}

.contact-form input,
.contact-form textarea {
  padding: 14px;
  border: none;
  border-bottom: 1px solid #ccc;
  font-size: 15px;
}

.contact-form textarea {
  resize: none;
  min-height: 90px;
}

.contact-form input:focus,
.contact-form textarea:focus {
  outline: none;
  border-color: var(--primary-color);
}

/* BUTTON */
.contact-form button {
  margin-top: 15px;
  background: #2ecc71;
  color: #fff;
  border: none;
  padding: 14px;
  font-size: 15px;
  cursor: pointer;
  border-radius: 4px;
}


/* ===== ANIMATION ===== */
.contact-header,
.contact-info,
.contact-form {
  opacity: 0;
  transform: translateY(40px);
  transition: all 0.9s ease;
}

.contact-section.in-view .contact-header,
.contact-section.in-view .contact-info,
.contact-section.in-view .contact-form {
  opacity: 1;
  transform: translateY(0);
}

/* ===== INNER BANNER ANIMATION ===== */
.inner-content,
.breadcrumb {
  opacity: 0;
  transition: all 0.9s ease;
}

.inner-content {
  transform: translateY(-40px);
}

.breadcrumb {
  transform: translateY(40px);
}

.inner-banner.animate .inner-content,
.inner-banner.animate .breadcrumb {
  opacity: 1;
  transform: translateY(0);
}
@media (max-width: 900px) {
  .contact-wrapper {
    grid-template-columns: 1fr;
  }

  .contact-left {
    min-height: 300px;
  }
}

/* ===== RESPONSIVE ===== */
@media (max-width: 992px) {
  .contact-wrapper {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .contact-section {
    padding: 70px 20px;
  }

  .inner-content h1 {
    font-size: 36px;
  }

  .breadcrumb {
    left: 16px;
    font-size: 12px;
  }
}
      `}</style>
    </>
  );
}

export default ContactUs;
